import { useRef } from "react"

import { Button, Typography } from "@mui/material"
import { Controller, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { BaseModal } from "@/components"
import FileDropZone from "@/components/FileDropZone"
import { useWebAdminTranslations } from "@/hooks"
import { useFileUpload } from "@/queries/hooks/useFileUpload"
import { compressImage, MAX_INPUT_SIZE_MB } from "@/utils/imageUtils"

import { PassHolderFormData } from "../../schema"

const ImageField = () => {
  const { t } = useTranslation()
  const { t: tWebAdmin } = useWebAdminTranslations()
  const pendingFileRef = useRef<File | null>(null)

  const { control, clearErrors, setValue } =
    useFormContext<PassHolderFormData>()
  const passHolderImage = useWatch({ control, name: "passHolderImage" })

  const { uploadFile, isUploading, error, resetError } = useFileUpload()

  // Image tracking logic:
  // - Existing image: has exitingPhotoId (set by WtpNumberField when autofilling)
  // - New image: has passHolderImage.id but no exitingPhotoId
  // The component handles both cases automatically through exitingPhotoId state

  const handleFileSelect = async (file: File) => {
    pendingFileRef.current = file

    const processedFile = await compressImage(file)

    uploadFile(
      {
        file: processedFile,
        localId: crypto.randomUUID(),
        fileType: "Photo",
      },
      {
        onSuccess: ({ fileId, base64 }) => {
          clearErrors("passHolderImage")
          setValue("passHolderImage", {
            id: fileId,
            base64,
            fileName: processedFile.name,
          })
          setValue("exitingPhotoId", null, {
            shouldValidate: false,
            shouldDirty: true,
          })
          pendingFileRef.current = null
        },
      }
    )
  }

  const handleRetry = () => {
    if (pendingFileRef.current) {
      handleFileSelect(pendingFileRef.current)
    }
  }

  return (
    <>
      <BaseModal
        open={!!error}
        onClose={resetError}
        title={t("passHolderDetails.form.errors.fileUploadFailedTitle")}
        footerContent={
          <Button variant="contained" onClick={() => handleRetry()}>
            {t("passHolderDetails.form.errors.fileUploadFailedRetry")}
          </Button>
        }
        maxWidth="sm"
      >
        <Typography variant="body2">
          {t("passHolderDetails.form.errors.fileUploadFailed")}
        </Typography>
      </BaseModal>
      <div data-field-name="passHolderImage" style={{ scrollMarginTop: 120 }}>
        <Controller
          name="passHolderImage"
          control={control}
          render={({ fieldState: { error } }) => (
            <FileDropZone
              textContent={{
                title: t("passHolderDetails.form.uploadImage"),
                acceptedFormatsText: t(
                  "passHolderDetails.form.acceptedFormatsText",
                  { maxSize: MAX_INPUT_SIZE_MB }
                ),
                learnMoreTitle: tWebAdmin(
                  "[SKIPASS_PROFILE_PHOTO_READ_MORE_HEADING]"
                ),
                learnMoreDescription: tWebAdmin(
                  "[SKIPASS_PROFILE_PHOTO_READ_MORE_DESCRIPTION]"
                ),
              }}
              acceptedFileTypes={["image/jpeg", "image/png"]}
              showPreview={true}
              imagePreviewData={passHolderImage?.base64}
              errorText={error?.message}
              isUploaded={!!passHolderImage?.id || !!passHolderImage?.base64}
              fileName={passHolderImage?.fileName}
              isUploading={isUploading}
              maxSize={MAX_INPUT_SIZE_MB}
              onFileSelect={handleFileSelect}
              onFileRemove={() => {
                clearErrors("passHolderImage")
                setValue("passHolderImage", undefined)
                setValue("exitingPhotoId", null, {
                  shouldValidate: false,
                  shouldDirty: true,
                })
              }}
            />
          )}
        />
      </div>
    </>
  )
}

export default ImageField
